/**
 * BL-2204 — the withheld-amount marker: the wire value for a stored amount whose
 * row currency is not supported, so it cannot be read without guessing one.
 *
 * Pure (ADR 010 — Isomorphic Services): logging the withheld amount is the
 * server's job (`createMoneyWithholder` in admin-services), not this module's.
 */
import type Decimal from 'decimal.js';

import { toWire, type MoneyWire } from './codec';
import { isSupportedCurrency } from './currency';
import type { Money } from './money';
import { storedMoney } from './stored-money';

/**
 * A stored amount the server could not read. It carries the offending currency
 * (so the reader can be told which) and deliberately NO amount, so no client
 * can format it as a number. It is never `null`: on every money DTO `null`
 * already means absent ("no sale", "uncapped") — a different fact.
 */
export interface WithheldMoneyWire {
    /** Why it was withheld. One reason today; the literal leaves room for more. */
    withheld: 'unsupported_currency';
    /** The row's stored currency code, which is not a supported currency. */
    currency: string;
}

/**
 * The visible word for a withheld value (BL-2204, approved by Michael Lawrie,
 * 2026-10-05). One source for the UI and for the server-composed summaries
 * that print it (BL-2482: the auction settings summary's buyer's premium).
 */
export const WITHHELD_AMOUNT_LABEL = 'Unavailable';

/** A money field that may be withheld: its wire value, or the marker. */
export type MoneyOrWithheldWire = MoneyWire | WithheldMoneyWire;

/**
 * The marker for an amount stored in `currency`.
 *
 * @param currency - The row's unsupported currency code.
 * @returns The marker.
 */
export function withheldMoney(currency: string): WithheldMoneyWire {
    return { withheld: 'unsupported_currency', currency };
}

/**
 * Whether a value is the withheld marker (and not a `MoneyWire`, `null`, or anything else).
 *
 * @param value - Any value off a DTO.
 * @returns True for the marker.
 */
export function isWithheldMoney(value: unknown): value is WithheldMoneyWire {
    return (
        typeof value === 'object' &&
        value !== null &&
        (value as { withheld?: unknown }).withheld === 'unsupported_currency' &&
        typeof (value as { currency?: unknown }).currency === 'string'
    );
}

/**
 * Read a stored amount with its own row's currency, or withhold it when that
 * currency is unsupported — never guessing one (ADR 060 — Money Type).
 *
 * @param amount - The stored amount.
 * @param currency - The same row's `currency`.
 * @returns The Money, or the marker.
 */
export function storedMoneyOrWithheld(amount: Decimal | string, currency: string): Money | WithheldMoneyWire {
    return isSupportedCurrency(currency) ? storedMoney(amount, currency) : withheldMoney(currency);
}

/**
 * Encode a read amount for the wire; the marker passes through unchanged.
 *
 * @param value - A Money, or the marker.
 * @returns Its wire form.
 */
export function toWireOrWithheld(value: Money | WithheldMoneyWire): MoneyOrWithheldWire {
    return isWithheldMoney(value) ? value : toWire(value);
}
